import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Camera, AlertTriangle, Users, Clock, MapPin, Trash2, ArrowUpRight, Car, ClipboardCheck } from 'lucide-react';
import DatePicker from '../components/DatePicker';
import { api } from '../api/client';
import { todayCalendarDate, zonedParts } from '../lib/calendar-date';
import { useTenantTimeZone } from '../hooks/useTenantTimeZone';
import { useAuth } from '../contexts/AuthContext';
import { useHideAgentFab } from '../components/AgentFab';
import { useMyWork } from '../hooks/useMyWork';
import { linkableItems, workItemLabel, workItemValue } from '../lib/work-item-selection';
import {
  REPORTABLE_INCIDENT_TYPES, INCIDENT_TYPE_LABELS, MOTOR_VEHICLE_TYPE,
  RECORDABLE_CLASS_OPTIONS, RECORDABLE_CLASS_LABELS, LOST_DAY_CLASSES,
  PREVENTABLE_OPTIONS, type RecordableClass,
} from '../lib/incident-types';
import { getUserDisplayName } from '../lib/user-display';
import { nearMissReportPath } from '../lib/safety-routes';

/**
 * The TENANT's wall-clock time now, 'HH:MM' — the incident-time seed. It used
 * to read the device clock (`toTimeString`). '' when the zone is unknown: an
 * input seed, never a rendered value. Local; no shared helper returns HH:MM.
 */
function tenantClockHHMM(timeZone: string | null): string {
  const p = zonedParts(new Date(), timeZone);
  if (!p) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(p.hour)}:${pad(p.minute)}`;
}

interface IncidentReportFormProps {
  onSubmit: (data: any) => void;
  onCancel: () => void;
}

type PersonInvolved = {
  userId: string;
  name: string;
  role: string;
  selected: boolean;
};

const ROLE_OPTIONS = ['Person involved', 'Witness', 'First responder', 'Supervisor'];

export default function IncidentReportForm({ onSubmit, onCancel }: IncidentReportFormProps) {
  useHideAgentFab();  // bottom action bar — the FAB would cover Submit.
  const { user, isFieldWorker, hasVertical } = useAuth();
  const timeZone = useTenantTimeZone();
  const navigate = useNavigate();

  // Load real team members
  const { data: usersData } = useQuery({
    queryKey: ['users-for-incident'],
    queryFn: () => api.users.list().then(r => r.data.data ?? r.data ?? []),
  });
  const users: any[] = usersData ?? [];

  /* Every job, every status — completed and cancelled included, and that is
   * DELIBERATE. An incident is reported after the fact, sometimes days after,
   * and the job it happened on may have finished in the meantime; filtering
   * those out would make it impossible to file against the job where the thing
   * actually occurred.
   *
   * The JSA form filters its picker down to schedulable jobs for the mirror
   * reason — it is written before work starts. The two pickers are meant to
   * disagree. See the matching note in JSAForm. */
  // Positive guards, one per vertical.
  const hasCasing = hasVertical('casing_running');
  const hasPt = hasVertical('pressure_testing');
  // GET /jobs is casing-gated: without casing it 403'd into an empty picker.
  const { data: jobsData } = useQuery({
    queryKey: ['jobs-for-incident'],
    queryFn: () => api.jobs.list().then(r => r.data.data ?? r.data ?? []),
    enabled: hasCasing,
  });
  const jobs: any[] = jobsData ?? [];
  // The hand's PT jobs, finished ones included — same reasoning as above: an
  // incident is reported after the fact (scope=all).
  const { data: myWork } = useMyWork('all', { enabled: hasPt });
  const ptItems = linkableItems(myWork?.items, 'pressure_testing');

  /* Fleet for the vehicle picker. Fetched unconditionally rather than on
   * selecting Motor vehicle — a driver filing from a weak signal should not
   * have to wait on a fresh request the moment they pick the type. */
  const { data: equipmentData } = useQuery({
    queryKey: ['equipment-for-incident'],
    queryFn: () => api.equipment.list().then(r => r.data.data ?? r.data ?? []),
  });
  const equipment: any[] = equipmentData ?? [];

  const userName = (u: any) =>
    `${u.first_name || u.firstName || ''} ${u.last_name || u.lastName || ''}`.trim() || u.email || '';

  const currentUserName = getUserDisplayName(user);

  const [formData, setFormData] = useState({
    // Canonical stored code, not a display string. The label comes from
    // INCIDENT_TYPE_LABELS at render time; the value goes to the API as-is.
    type: REPORTABLE_INCIDENT_TYPES[0] as string,
    date: todayCalendarDate(timeZone) ?? '',
    time: tenantClockHHMM(timeZone),
    location: '',
    job: '',
    description: '',
    immediateActions: '',
    rootCause: '',
    preventiveActions: '',
    severity: 'Low',
    reportedBy: currentUserName,

    /* ── Classification (0312) ──
     * `recordableClass` starts as '' meaning NOT YET CLASSIFIED, and that is a
     * real state, not a missing one. It is deliberately not pre-set to
     * 'not_recordable': that value means a human reviewed the incident and
     * found it non-recordable, and defaulting to it would let an unreviewed
     * backlog read as a clean safety record on every rate. Submitted as
     * `undefined`, which the API stores as NULL. */
    recordableClass: '' as RecordableClass | '',
    daysAway: '',
    daysRestricted: '',
    returnToWorkDate: '',

    /* ── Motor vehicle ── */
    equipmentId: '',
    vehicleRepairCost: '',
    /* null is "not yet reviewed" and is NOT the same as false. Only `true`
     * counts toward MVIR; `false` is a reviewed verdict that must survive the
     * round trip. The API stores this with `?? null` for the same reason. */
    isPreventable: null as boolean | null,
  });

  const isMotorVehicle = formData.type === MOTOR_VEHICLE_TYPE;
  const showLostDays = LOST_DAY_CLASSES.includes(formData.recordableClass as RecordableClass);

  const [peopleInvolved, setPeopleInvolved] = useState<PersonInvolved[]>([]);

  const availableUsers = users.filter(
    (u) => u.role !== 'super_admin' && !peopleInvolved.some((p) => p.userId === u.id),
  );

  const addPerson = (userId: string) => {
    if (!userId) return;
    const u = users.find((x: any) => x.id === userId);
    if (!u) return;
    setPeopleInvolved((prev) => [
      ...prev,
      { userId: u.id, name: userName(u), role: 'Witness', selected: true },
    ]);
  };

  const updateRole = (userId: string, role: string) => {
    setPeopleInvolved((prev) => prev.map((p) => (p.userId === userId ? { ...p, role } : p)));
  };

  const removePerson = (userId: string) => {
    setPeopleInvolved((prev) => prev.filter((p) => p.userId !== userId));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ ...formData, people: peopleInvolved });
  };

  const severityColors: Record<string, string> = {
    'Low': 'bg-fieldiq-success/20 text-fieldiq-success',
    'Medium': 'bg-fieldiq-accent/20 text-fieldiq-accent',
    'High': 'bg-orange-500/20 text-orange-500',
    'Critical': 'bg-fieldiq-danger/20 text-fieldiq-danger',
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 pb-4">
      {/* Incident Type & Severity */}
      <div className="bg-fieldiq-card rounded-xl p-4">
        <h3 className="font-semibold mb-3 flex items-center gap-2">
          <AlertTriangle size={18} className="text-fieldiq-danger" />
          Incident Details
        </h3>
        <div className="space-y-3">
          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Type</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2"
            >
              {REPORTABLE_INCIDENT_TYPES.map((t) => (
                <option key={t} value={t}>{INCIDENT_TYPE_LABELS[t]}</option>
              ))}
            </select>
            {/* Near Miss used to sit at the top of this list, and it was the
              * default. Removing it without a signpost sends anyone who came
              * here to file one hunting through a form that no longer has the
              * option — so point at the report that replaced it.
              *
              * The target is persona-dependent, and NOT a literal. This form is
              * rendered in all three route trees, and `/safety` is registered
              * only in the AppLayout one — so for a field worker this link used
              * to resolve to their tree's catch-all and bounce them home.
              * Repointed rather than registering `/safety` in the field trees:
              * the rule is register when no field-specific variant exists,
              * repoint when one does, and `/field/safety` already exists in
              * both field trees. See `lib/safety-routes.ts`. */}
            <button
              type="button"
              onClick={() => navigate(nearMissReportPath(isFieldWorker))}
              className="mt-2 flex items-center gap-1 text-xs text-fieldiq-accent hover:underline"
            >
              Reporting a near miss? Use the Near Miss report
              <ArrowUpRight size={13} />
            </button>
          </div>
          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Severity</label>
            <div className="grid grid-cols-2 gap-2">
              {['Low', 'Medium', 'High', 'Critical'].map((level) => (
                <button
                  key={level}
                  type="button"
                  onClick={() => setFormData({ ...formData, severity: level })}
                  className={`py-2 rounded-lg text-sm font-medium ${
                    formData.severity === level ? severityColors[level] : 'bg-fieldiq-dark text-fieldiq-text'
                  }`}
                >
                  {level}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Vehicle Details — only for a motor vehicle incident.
        * Revealed rather than always shown: a repair cost box on a slip-and-fall
        * is noise, and a vehicle picker there invites a wrong association. */}
      {isMotorVehicle && (
        <div className="bg-fieldiq-card rounded-xl p-4">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Car size={18} className="text-fieldiq-accent" />
            Vehicle Details
          </h3>
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Vehicle</label>
              <select
                value={formData.equipmentId}
                onChange={(e) => setFormData({ ...formData, equipmentId: e.target.value })}
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2"
              >
                <option value="">-- Select a vehicle (optional) --</option>
                {equipment.map((eq: any) => (
                  <option key={eq.id} value={eq.id}>
                    {eq.unit_number || eq.unitNumber || eq.name || eq.id}
                    {(eq.license_plate || eq.licensePlate) ? ` — ${eq.license_plate || eq.licensePlate}` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Repair cost</label>
              <input
                type="number" min="0" step="any" inputMode="decimal"
                value={formData.vehicleRepairCost}
                onChange={(e) => setFormData({ ...formData, vehicleRepairCost: e.target.value })}
                placeholder="Leave blank if not yet known"
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 placeholder:text-fieldiq-muted"
              />
              {/* Blank is not zero. An unknown repair cost left as 0 would read
                * as a collision that cost nothing. */}
              <p className="text-xs text-fieldiq-muted mt-1">
                Leave blank until the cost is known — blank is recorded as unknown, not as $0.
              </p>
            </div>

            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Preventable</label>
              <div className="grid grid-cols-3 gap-2">
                {PREVENTABLE_OPTIONS.map((opt) => (
                  <button
                    key={String(opt.value)}
                    type="button"
                    onClick={() => setFormData({ ...formData, isPreventable: opt.value })}
                    className={`py-2 rounded-lg text-sm font-medium ${
                      formData.isPreventable === opt.value
                        ? 'bg-fieldiq-accent text-fieldiq-dark'
                        : 'bg-fieldiq-dark text-fieldiq-text'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {/* Three states, and the middle one is load bearing. "Not yet
                * reviewed" is the default and is NOT "No" — only Yes counts
                * toward MVIR, so mistaking one for the other either inflates
                * the rate or hides a collision from the review queue. */}
              <p className="text-xs text-fieldiq-muted mt-1">
                "Not yet reviewed" is the default and is not the same as "No". Only "Yes" counts
                toward the motor vehicle incident rate.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Recordable Classification */}
      <div className="bg-fieldiq-card rounded-xl p-4">
        <h3 className="font-semibold mb-3 flex items-center gap-2">
          <ClipboardCheck size={18} />
          Recordable Classification
        </h3>
        <div className="space-y-3">
          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Classification</label>
            <select
              value={formData.recordableClass}
              onChange={(e) => setFormData({
                ...formData,
                recordableClass: e.target.value as RecordableClass | '',
              })}
              className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2"
            >
              {/* The unselected default. Submits nothing, so the incident is
                * stored unclassified and shows up in the review backlog rather
                * than silently counting as non-recordable. */}
              <option value="">-- Not yet classified --</option>
              {RECORDABLE_CLASS_OPTIONS.map((c) => (
                <option key={c} value={c}>{RECORDABLE_CLASS_LABELS[c]}</option>
              ))}
            </select>
            <p className="text-xs text-fieldiq-muted mt-1">
              First aid is <span className="font-medium">not</span> recordable and does not count
              toward TRIF. Leave this unset if nobody has classified the incident yet.
            </p>
          </div>

          {/* Days are only meaningful once someone has said the injury kept
            * somebody off work or on modified duties. Asked then, not before. */}
          {showLostDays && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-fieldiq-text mb-1">Days away</label>
                <input
                  type="number" min="0" step="1" inputMode="numeric"
                  value={formData.daysAway}
                  onChange={(e) => setFormData({ ...formData, daysAway: e.target.value })}
                  className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm text-fieldiq-text mb-1">Days restricted</label>
                <input
                  type="number" min="0" step="1" inputMode="numeric"
                  value={formData.daysRestricted}
                  onChange={(e) => setFormData({ ...formData, daysRestricted: e.target.value })}
                  className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Return to work date</label>
            <DatePicker
              value={formData.returnToWorkDate}
              onChange={(v) => setFormData({ ...formData, returnToWorkDate: v })}
            />
          </div>
        </div>
      </div>

      {/* When & Where */}
      <div className="bg-fieldiq-card rounded-xl p-4">
        <h3 className="font-semibold mb-3 flex items-center gap-2">
          <Clock size={18} />
          When & Where
        </h3>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Date</label>
              <DatePicker
                value={formData.date}
                onChange={(v) => setFormData({ ...formData, date: v })}
              />
            </div>
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Time</label>
              <input
                type="time"
                value={formData.time}
                onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Location</label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              placeholder="e.g. Well site, shop yard"
              className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Job</label>
            <select
              value={formData.job}
              onChange={(e) => setFormData({ ...formData, job: e.target.value })}
              className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2"
            >
              <option value="">-- Select a job (optional) --</option>
              {jobs.map((j: any) => (
                <option key={j.id} value={j.id}>
                  {j.job_number || j.jobNumber || j.id} - {j.customer_name || j.customerName || j.well_name || 'Untitled'}
                </option>
              ))}
              {ptItems.map((item) => (
                <option key={workItemValue(item)} value={workItemValue(item)}>
                  {workItemLabel(item)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Description */}
      <div className="bg-fieldiq-card rounded-xl p-4">
        <h3 className="font-semibold mb-3">Incident Description</h3>
        <textarea
          rows={4}
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Describe what happened..."
          className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2"
        />
      </div>

      {/* People Involved */}
      <div className="bg-fieldiq-card rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold flex items-center gap-2">
            <Users size={18} />
            People Involved
          </h3>
          {availableUsers.length > 0 && (
            <select
              value=""
              onChange={(e) => { addPerson(e.target.value); e.target.value = ''; }}
              className="bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-1.5 text-sm text-amber-400"
            >
              <option value="">+ Add Person</option>
              {availableUsers.map((u: any) => (
                <option key={u.id} value={u.id}>{userName(u)}</option>
              ))}
            </select>
          )}
        </div>

        {peopleInvolved.length === 0 ? (
          <p className="text-sm text-fieldiq-muted">No people added yet. Use "Add Person" above.</p>
        ) : (
          <div className="space-y-2">
            {peopleInvolved.map((person) => (
              <div key={person.userId} className="flex items-center gap-3 p-3 bg-fieldiq-dark rounded-lg">
                <div className="w-8 h-8 rounded-full bg-fieldiq-accent flex items-center justify-center text-fieldiq-dark font-bold text-sm">
                  {person.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-white text-sm truncate">{person.name}</div>
                  <select
                    value={person.role}
                    onChange={(e) => updateRole(person.userId, e.target.value)}
                    className="bg-fieldiq-card border border-fieldiq-border rounded px-2 py-0.5 text-xs text-fieldiq-text mt-1"
                  >
                    {ROLE_OPTIONS.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  onClick={() => removePerson(person.userId)}
                  className="text-red-400 hover:text-red-300 p-1"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Immediate Actions */}
      <div className="bg-fieldiq-card rounded-xl p-4">
        <h3 className="font-semibold mb-3">Immediate Actions Taken</h3>
        <textarea
          rows={3}
          value={formData.immediateActions}
          onChange={(e) => setFormData({ ...formData, immediateActions: e.target.value })}
          placeholder="What was done immediately after the incident..."
          className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2"
        />
      </div>

      {/* Root Cause */}
      <div className="bg-fieldiq-card rounded-xl p-4">
        <h3 className="font-semibold mb-3">Root Cause</h3>
        <textarea
          rows={3}
          value={formData.rootCause}
          onChange={(e) => setFormData({ ...formData, rootCause: e.target.value })}
          placeholder="Why did this happen..."
          className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2"
        />
      </div>

      {/* Preventive Actions */}
      <div className="bg-fieldiq-card rounded-xl p-4">
        <h3 className="font-semibold mb-3">Preventive Actions</h3>
        <textarea
          rows={3}
          value={formData.preventiveActions}
          onChange={(e) => setFormData({ ...formData, preventiveActions: e.target.value })}
          placeholder="How to prevent this in the future..."
          className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2"
        />
      </div>

      {/* Photos/Evidence */}
      <div className="bg-fieldiq-card rounded-xl p-4">
        <h3 className="font-semibold mb-3 flex items-center gap-2">
          <Camera size={18} />
          Photos / Evidence
        </h3>
        <div className="grid grid-cols-3 gap-3">
          <button type="button" className="border-2 border-dashed border-fieldiq-border rounded-lg p-4 text-center hover:border-fieldiq-accent transition">
            <Camera size={20} className="mx-auto mb-1 text-fieldiq-muted" />
            <span className="text-xs text-fieldiq-text">Scene</span>
          </button>
          <button type="button" className="border-2 border-dashed border-fieldiq-border rounded-lg p-4 text-center hover:border-fieldiq-accent transition">
            <AlertTriangle size={20} className="mx-auto mb-1 text-fieldiq-muted" />
            <span className="text-xs text-fieldiq-text">Conditions</span>
          </button>
          <button type="button" className="border-2 border-dashed border-fieldiq-border rounded-lg p-4 text-center hover:border-fieldiq-accent transition">
            <MapPin size={20} className="mx-auto mb-1 text-fieldiq-muted" />
            <span className="text-xs text-fieldiq-text">Location</span>
          </button>
        </div>
      </div>

      {/* Reported By */}
      <div className="bg-fieldiq-card rounded-xl p-4">
        <h3 className="font-semibold mb-3">Reported By</h3>
        <p className="text-white">{currentUserName || 'Current user'}</p>
      </div>

      {/* Actions */}
      <div className="border-t border-fieldiq-border px-4 pt-4 pb-[var(--bottom-chrome)] flex gap-3 mt-4">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 bg-fieldiq-border text-white py-3 rounded-lg font-medium"
        >
          Save Draft
        </button>
        <button
          type="submit"
          className="flex-1 bg-fieldiq-accent text-fieldiq-dark py-3 rounded-lg font-medium"
        >
          Submit Report
        </button>
      </div>
    </form>
  );
}
